import { memo, useState } from 'react';
import { X, ArrowUpRight, Palette, Smartphone, Database, Layers, Trash2, KeyRound, Type } from 'lucide-react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';

// Static projects data - defined outside component to prevent recreation on each render
const PROJECTS = [
  {
    title: 'Palette Generator',
    description: 'Generates accessible colour palettes based on a single colour input. Handy for quick UI prototyping.',
    url: 'https://palette-generator.significa.co/',
    icon: Palette,
  },
  {
    title: 'App Distribution Server',
    description: 'Self-hosted tool for sharing internal iOS/Android builds without TestFlight or Expo.',
    url: 'https://github.com/significa/app-distribution-server',
    icon: Smartphone,
  },
  {
    title: 'Fly pg_dump to S3',
    description: 'PostgreSQL logical backups to S3 with great retention policies for Fly.io apps.',
    url: 'https://github.com/significa/fly-pg-dump-to-s3',
    icon: Database,
  },
  {
    title: 'Foundations',
    description: 'Our base config for web projects — Prettier, TypeScript, ESLint, and Vitest, all wired up.',
    url: 'https://foundations.significa.co/about',
    icon: Layers,
  },
  {
    title: 'Storyblok Assets Clean-up',
    description: 'Script to find and delete unused assets in Storyblok. Keeps your CMS lean and bills lower.',
    url: 'https://github.com/significa/storyblok-assets-cleanup',
    icon: Trash2,
  },
  {
    title: '1Password Secrets',
    description: 'CLI tool to sync secrets from 1Password into local .env files. Safer secret management.',
    url: 'https://github.com/significa/1password-secrets',
    icon: KeyRound,
  },
  {
    title: 'Variable Icon Font',
    description: 'Open-source variable icon font built to pair with Inter and adapt to any UI.',
    url: 'https://significa.co/blog/signals-by-significa',
    icon: Type,
  },
];

/**
 * ProjectCard Component
 *
 * Clean, minimal card with icon, title, description and arrow indicator
 */
const ProjectCard = memo(({ title, description, url, icon: Icon }) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        padding: spacing.md,
        textDecoration: 'none',
        background: isHovered ? colors.backgroundOffset : 'transparent',
        borderRadius: borderRadius.md,
        transition: 'background 0.15s ease',
        cursor: 'pointer',
      }}
    >
      {/* Icon */}
      <div style={{
        width: 36,
        height: 36,
        borderRadius: borderRadius.sm,
        background: 'transparent',
        border: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {Icon && <Icon size={18} style={{ color: colors.foregroundSecondary }} />}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{
          margin: 0,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: colors.foreground,
          letterSpacing: '-0.01em',
          fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          {title}
        </h3>
        <p style={{
          margin: 0,
          marginTop: 2,
          fontSize: '0.75rem',
          color: colors.foregroundSecondary,
          lineHeight: 1.4,
          letterSpacing: '-0.005em',
          fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          {description}
        </p>
      </div>

      {/* Arrow indicator */}
      <ArrowUpRight
        size={16}
        style={{
          color: isHovered ? colors.foreground : colors.foregroundTertiary,
          transition: 'all 0.15s ease',
          transform: isHovered ? 'translate(2px, -2px)' : 'translate(0, 0)',
          flexShrink: 0,
        }}
      />
    </a>
  );
});

/**
 * ProjectsDrawer Component
 *
 * A drawer that slides in from the left showing Significa projects
 */
const ProjectsDrawer = ({ isOpen, onClose }) => {
  const { colors } = useTheme();

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: colors.isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.4)',
            zIndex: 999,
            animation: 'fadeIn 0.2s ease-out'
          }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: '460px',
          maxWidth: '90vw',
          background: colors.background,
          borderRight: `1px solid ${colors.border}`,
          zIndex: 1000,
          transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOpen
            ? `4px 0 24px ${colors.isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)'}`
            : 'none'
        }}
      >
        {/* Header */}
        <div style={{
          padding: `${spacing.lg} ${spacing.xl}`,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '1rem',
              fontWeight: 600,
              color: colors.foreground,
              letterSpacing: '-0.02em',
            }}>
              More from Significa
            </h2>
            <p style={{
              margin: 0,
              marginTop: 2,
              fontSize: '0.8125rem',
              color: colors.foregroundSecondary,
              letterSpacing: '-0.005em',
            }}>
              Explore our other projects
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: borderRadius.sm,
              cursor: 'pointer',
              color: colors.foregroundSecondary,
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.backgroundOffset;
              e.currentTarget.style.color = colors.foreground;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.foregroundSecondary;
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: `${spacing.md} ${spacing.lg}`,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.xs,
          }}>
            {PROJECTS.map((project, index) => (
              <ProjectCard key={index} {...project} />
            ))}
          </div>
        </div>
      </div>

      {/* Global styles for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default ProjectsDrawer;
